import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Cell, Grid } from 'styled-css-grid';
import get from 'lodash/get';

import Input from '@xcritical/input';
import { formSelector, FormField } from '@xcritical/forms';

import { createUrl } from '@crm-framework/url';
import { CopyIconButton } from '@crm-framework/copy-icon-button';
import { Label } from '@crm-framework/label';

import {
  DictionarySelect,
  getDictionaryByNameSelector,
  IStoreWithDictionary,
} from '@crm-package/dictionaries';

import { getMyAccountUrlByEnvironmentId, isNewPromo } from '../utils';
import { IPromoDictionaries, IPromoLinkProps } from '../types';
import { PROMO_FORM_NAME } from '../consts';

export const PromoLinkComponent: React.FC<IPromoLinkProps> = ({ id }) => {
  const { myAccountUrls, guid, environmentId } = useSelector(
    (state: IStoreWithDictionary<IPromoDictionaries>) => {
      const promoForm = formSelector(state, PROMO_FORM_NAME);
      const promoGuid = get(promoForm, ['model', 'guid'], '');
      const environmentForLink = get(
        promoForm,
        ['model', 'environmentForLink'],
        ''
      );
      const myAccountUrlsDictionary = getDictionaryByNameSelector(
        state,
        'myAccountUrls'
      );

      return {
        guid: promoGuid,
        myAccountUrls: myAccountUrlsDictionary,
        environmentId: environmentForLink,
      };
    }
  );

  const promoIsNew = isNewPromo(id);

  const url = useMemo(() => {
    if (promoIsNew) {
      return {
        error: 'You can copy the value after creating the promo.',
      };
    }

    if (!environmentId) {
      return {
        error: 'Please select Brand to get the link.',
      };
    }

    return {
      url: createUrl({
        href: getMyAccountUrlByEnvironmentId(myAccountUrls, environmentId),
        query: {
          Promo: guid,
        },
      }),
    };
  }, [environmentId, myAccountUrls, promoIsNew]);

  return (
    <>
      <Cell>
        <Grid>
          <Cell>
            <Label htmlFor="environmentForLink">Link</Label>
          </Cell>
          <Cell>
            <FormField
              inputId="environmentForLink"
              openMenuOnFocus
              name="environmentForLink"
              dictionaryName="environment"
              placeholder="Environments"
              component={DictionarySelect}
              disabled={promoIsNew}
              appearance="promo-form-environments"
            />
          </Cell>
        </Grid>
      </Cell>
      <Cell>
        <Input
          spellCheck={false}
          name="link"
          value={url.url || url.error}
          placeholder="Link"
          shouldFitContainer
          prefix={
            <CopyIconButton text={url.url || ''} disabled={!!url.error} />
          }
        />
      </Cell>
    </>
  );
};
